import { api } from '@verifime/api-definition';
import { DocumentType, TDocument, TIdentityDocumentType } from '@verifime/utils';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { TDocumentTypeChoice, TIdentityDocumentsUploadConfig } from './types';

type WizardState = {
  customerId: string | null;
  countryCode: string | null;
  uploadIdDocumentConfig: TIdentityDocumentsUploadConfig | null;
  identityDocumentTypeSelections: TIdentityDocumentType[];
};

type WizardAction =
  | { type: 'SET_IDENTITY_DOCUMENT_TYPE_SELECTIONS'; payload: TIdentityDocumentType[] }
  | {
      type: 'UPDATE_IDENTITY_DOCUMENT_TYPE_SELECTION';
      payload: { stepIndex: number; newDocumentType: TIdentityDocumentType };
    }
  | { type: 'RESET' };

const initialState: WizardState = {
  customerId: null,
  countryCode: null,
  uploadIdDocumentConfig: null,
  identityDocumentTypeSelections: [],
};

function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case 'SET_IDENTITY_DOCUMENT_TYPE_SELECTIONS':
      return { ...state, identityDocumentTypeSelections: action.payload };
    case 'UPDATE_IDENTITY_DOCUMENT_TYPE_SELECTION': {
      const { stepIndex, newDocumentType } = action.payload;
      const newSelections = [...state.identityDocumentTypeSelections];

      // Check if the new document type already exists in other positions
      const existingIndex = newSelections.findIndex(
        (selection, index) => index !== stepIndex && selection === newDocumentType,
      );

      if (existingIndex !== -1 && state.uploadIdDocumentConfig) {
        // If the new document type exists elsewhere, we need to replace it with a default value
        const { documentTypeChoices } = state.uploadIdDocumentConfig;

        // Create a temporary selection array that includes the new document type at the target position
        // but excludes the existing duplicate
        const tempSelections = [...newSelections];
        tempSelections[stepIndex] = newDocumentType;
        tempSelections[existingIndex] = null; // Temporarily clear the duplicate

        // Get all currently selected types excluding null values
        const selectedTypesExcludingDuplicate = tempSelections.filter(
          (selection) => selection !== null,
        ) as DocumentType[];

        // Find available document types that are not already selected
        const availableChoices = getDocumentTypesExcludeSelectedOnes({
          selectedDocumentTypes: selectedTypesExcludingDuplicate,
          documentTypeChoices,
        });

        // Replace the existing occurrence with the first available default
        if (availableChoices.length > 0) {
          newSelections[existingIndex] = availableChoices[0].type.code as TIdentityDocumentType;
        } else {
          // Clear the existing occurrence
          newSelections[existingIndex] = null;
        }
      }

      // Set the new document type at the target position
      newSelections[stepIndex] = newDocumentType;

      return { ...state, identityDocumentTypeSelections: newSelections };
    }
    case 'RESET':
      return {
        ...state,
        identityDocumentTypeSelections: [],
      };
    default:
      return state;
  }
}

type WizardContextValue = {
  state: WizardState;
  setIdentityDocumentTypeSelections: (methods: TIdentityDocumentType[]) => void;
  updateIdentityDocumentTypeSelection: (stepIndex: number, method: TIdentityDocumentType) => void;
  reset: () => void;
  getAvailableDocumentTypesForStep: (stepIndex: number) => TDocument[];
  getSelectedDocumentTypeForStep: (stepIndex: number) => TIdentityDocumentType;
  getUploadRequiredForSelectedDocumentTypeForStep: (stepIndex: number) => boolean;
  getExpiredDocumentAcceptedForSelectedDocumentTypeForStep: (stepIndex: number) => boolean;
};

const findChoiceOfType = (
  config: TIdentityDocumentsUploadConfig | null,
  documentType: TIdentityDocumentType | undefined,
): TDocumentTypeChoice | undefined =>
  config?.documentTypeChoices.find((choice) => choice.type.code === documentType);

const WizardContext = createContext<WizardContextValue | undefined>(undefined);

interface WizardProviderProps {
  children: ReactNode;
  initialConfig?: TIdentityDocumentsUploadConfig;
  customerId: string | null;
  countryCode: string | null;
}

export const WizardProvider: React.FC<WizardProviderProps> = ({
  children,
  initialConfig,
  customerId,
  countryCode,
}) => {
  const [state, dispatch] = useReducer(wizardReducer, {
    ...initialState,
    uploadIdDocumentConfig: initialConfig || null,
    customerId,
    countryCode,
  });

  const setIdentityDocumentTypeSelections = useCallback((methods: TIdentityDocumentType[]) => {
    dispatch({ type: 'SET_IDENTITY_DOCUMENT_TYPE_SELECTIONS', payload: methods });
  }, []);

  const updateIdentityDocumentTypeSelection = useCallback(
    (stepIndex: number, method: TIdentityDocumentType) => {
      dispatch({
        type: 'UPDATE_IDENTITY_DOCUMENT_TYPE_SELECTION',
        payload: { stepIndex, newDocumentType: method },
      });
    },
    [],
  );

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  useEffect(() => {
    if (!state.uploadIdDocumentConfig) {
      return;
    }

    const initializeDocumentTypeSelections = (userSavedSelections: TIdentityDocumentType[]) => {
      try {
        const { documentTypeChoices, documentCount } = state.uploadIdDocumentConfig;
        const supportedDocumentTypes = documentTypeChoices.map((choice) => choice.type);

        let finalSelections: TIdentityDocumentType[];

        if (userSavedSelections.length > 0) {
          const validSavedSelections = userSavedSelections.filter((selection) =>
            supportedDocumentTypes.some((type) => type.code === selection),
          );

          if (validSavedSelections.length >= documentCount) {
            finalSelections = validSavedSelections.slice(0, documentCount);
          } else {
            const defaultDocumentTypes = getDefaultDocumentTypes(
              supportedDocumentTypes,
              documentCount,
            );
            finalSelections = [
              ...validSavedSelections,
              ...defaultDocumentTypes
                .filter((type) => !validSavedSelections.includes(type as TIdentityDocumentType))
                .slice(0, documentCount - validSavedSelections.length),
            ] as TIdentityDocumentType[];
          }
        } else {
          const defaultDocumentTypes = getDefaultDocumentTypes(
            supportedDocumentTypes,
            documentCount,
          );
          finalSelections = defaultDocumentTypes as TIdentityDocumentType[];
        }

        dispatch({ type: 'SET_IDENTITY_DOCUMENT_TYPE_SELECTIONS', payload: finalSelections });
      } catch (error) {
        console.error('Failed to initialize document type selections:', error);
        const { documentTypeChoices, documentCount } = state.uploadIdDocumentConfig;
        const supportedDocumentTypes = documentTypeChoices.map((choice) => choice.type);
        const defaultDocumentTypes = getDefaultDocumentTypes(supportedDocumentTypes, documentCount);

        dispatch({
          type: 'SET_IDENTITY_DOCUMENT_TYPE_SELECTIONS',
          payload: defaultDocumentTypes as TIdentityDocumentType[],
        });
      }
    };

    api
      .getV2personIdidentityDocumentselection({ params: { id: state.customerId } })
      .then((data) => {
        if (data) {
          initializeDocumentTypeSelections(data);
        }
      })
      .catch(() => {
        initializeDocumentTypeSelections([]);
      });
  }, [state.customerId, state.uploadIdDocumentConfig]);

  const getAvailableDocumentTypesForStep = useCallback(
    (stepIndex: number): TDocument[] => {
      if (!state.uploadIdDocumentConfig) {
        return [];
      }

      const { documentTypeChoices } = state.uploadIdDocumentConfig;

      const selectedTypes = state.identityDocumentTypeSelections
        .slice(0, stepIndex)
        .filter(Boolean);

      const filteredTypes = getDocumentTypesExcludeSelectedOnes({
        selectedDocumentTypes: selectedTypes as DocumentType[],
        documentTypeChoices,
      });

      return sortDocumentTypesByPriority(filteredTypes.map((choice) => choice.type));
    },
    [state.uploadIdDocumentConfig, state.identityDocumentTypeSelections],
  );

  const getSelectedDocumentTypeForStep = useCallback(
    (stepIndex: number): TIdentityDocumentType => {
      return state.identityDocumentTypeSelections[stepIndex];
    },
    [state.identityDocumentTypeSelections],
  );

  const getUploadRequiredForSelectedDocumentTypeForStep = useCallback(
    (stepIndex: number): boolean => {
      if (!state.uploadIdDocumentConfig) {
        return false;
      }
      const selectedDocumentType = getSelectedDocumentTypeForStep(stepIndex);
      if (!selectedDocumentType) {
        return false;
      }
      const { documentTypeChoices } = state.uploadIdDocumentConfig;
      const selectedChoice = documentTypeChoices.find(
        (choice) => choice.type.code === selectedDocumentType,
      );
      return selectedChoice?.rules?.uploadRequired ?? false;
    },
    [state.uploadIdDocumentConfig, getSelectedDocumentTypeForStep],
  );

  // Whether every organisation of the customer accepts an expired document of the type selected
  // for this step. False when nothing says so: a type missing from the config, a config still to
  // load, or a rule the server left out (VME-4254).
  const getExpiredDocumentAcceptedForSelectedDocumentTypeForStep = useCallback(
    (stepIndex: number): boolean => {
      const selectedChoice = findChoiceOfType(
        state.uploadIdDocumentConfig,
        getSelectedDocumentTypeForStep(stepIndex),
      );
      return selectedChoice?.rules?.expiredDocumentAccepted ?? false;
    },
    [state.uploadIdDocumentConfig, getSelectedDocumentTypeForStep],
  );

  const contextValue: WizardContextValue = useMemo(
    () => ({
      state: {
        customerId: state.customerId,
        countryCode: state.countryCode,
        uploadIdDocumentConfig: state.uploadIdDocumentConfig,
        identityDocumentTypeSelections: state.identityDocumentTypeSelections,
      },
      setIdentityDocumentTypeSelections,
      updateIdentityDocumentTypeSelection,
      reset,
      getAvailableDocumentTypesForStep,
      getSelectedDocumentTypeForStep,
      getUploadRequiredForSelectedDocumentTypeForStep,
      getExpiredDocumentAcceptedForSelectedDocumentTypeForStep,
    }),
    [
      state.customerId,
      state.countryCode,
      state.uploadIdDocumentConfig,
      state.identityDocumentTypeSelections,
      setIdentityDocumentTypeSelections,
      updateIdentityDocumentTypeSelection,
      reset,
      getAvailableDocumentTypesForStep,
      getSelectedDocumentTypeForStep,
      getExpiredDocumentAcceptedForSelectedDocumentTypeForStep,
    ],
  );

  return <WizardContext.Provider value={contextValue}>{children}</WizardContext.Provider>;
};

export const useWizardContext = (): WizardContextValue => {
  const context = useContext(WizardContext);
  if (!context) {
    throw new Error('useWizardContext must be used within a WizardProvider');
  }
  return context;
};

export const getDocumentTypesExcludeSelectedOnes = ({
  selectedDocumentTypes,
  documentTypeChoices,
}: {
  selectedDocumentTypes: DocumentType[];
  documentTypeChoices: TDocumentTypeChoice[];
}): TDocumentTypeChoice[] => {
  return documentTypeChoices.filter(
    (choice) => !selectedDocumentTypes.includes(choice.type.code as DocumentType),
  );
};

export const getDefaultDocumentTypes = (
  supportedDocumentTypes: TDocument[],
  documentCount: number,
): DocumentType[] => {
  if (supportedDocumentTypes.length === 0) {
    return [];
  }

  const orderedDocumentTypes = sortDocumentTypesByPriority(supportedDocumentTypes);

  if (documentCount === 1) {
    return [orderedDocumentTypes[0].code as DocumentType];
  }
  const defaultTypes: DocumentType[] = [orderedDocumentTypes[0].code as DocumentType];
  const usedTypes = new Set([defaultTypes[0]]);
  for (let i = 1; i < documentCount && i < orderedDocumentTypes.length; i++) {
    const availableType = orderedDocumentTypes.find(
      (type) => !usedTypes.has(type.code as DocumentType),
    );
    if (availableType) {
      defaultTypes.push(availableType.code as DocumentType);
      usedTypes.add(availableType.code as DocumentType);
    } else {
      defaultTypes.push(orderedDocumentTypes[i % orderedDocumentTypes.length].code as DocumentType);
    }
  }
  return defaultTypes;
};

function sortDocumentTypesByPriority(documentTypes: TDocument[]): TDocument[] {
  return documentTypes.sort((a, b) => {
    if (a.code === 'DriverLicence') return -1;
    if (b.code === 'DriverLicence') return 1;
    if (a.code === 'Passport') return -1;
    if (b.code === 'Passport') return 1;
    return 0;
  });
}

export default WizardContext;
